import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import StudioSidebar from '../../components/ui/StudioSidebar';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';

const StudioSDK = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [apiKeys, setApiKeys] = useState([]);

  // Get tab from URL query parameter, default to 'overview'
  const tabParam = searchParams.get('tab');
  const validTabs = ['overview', 'javascript', 'unity', 'testing', 'examples'];
  const initialTab = validTabs.includes(tabParam) ? tabParam : 'overview';

  const [selectedTab, setSelectedTab] = useState(initialTab);
  const [copySuccess, setCopySuccess] = useState('');

  // Check if user is a studio admin (for showing API keys)
  const isStudioAdmin = user && userProfile?.role === 'studio_admin';

  useEffect(() => {
    // Only load API keys if user is a studio admin
    if (isStudioAdmin) {
      loadAPIKeys();
    }
  }, [isStudioAdmin]);

  // Update tab when URL parameter changes
  useEffect(() => {
    if (tabParam && validTabs.includes(tabParam)) {
      setSelectedTab(tabParam);
    }
  }, [tabParam]);

  const loadAPIKeys = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/studio/api-keys', {
        headers: {
          'Authorization': `Bearer ${user.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setApiKeys(data.apiKeys || []);
      }
    } catch (error) {
      console.error('Failed to load API keys:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(type);
      setTimeout(() => setCopySuccess(''), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const tabs = [
    { id: 'overview', name: 'Overview', icon: 'Info' },
    { id: 'javascript', name: 'JavaScript', icon: 'Code' },
    { id: 'unity', name: 'Unity', icon: 'Box' },
    { id: 'testing', name: 'Testing', icon: 'TestTube' },
    { id: 'examples', name: 'Examples', icon: 'BookOpen' }
  ];

  const primaryApiKey = apiKeys.find(key => key.key_name === 'Production API Key')?.api_key || 'YOUR_API_KEY';

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading SDK documentation...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Show Header for public, StudioSidebar for studio admins */}
      {isStudioAdmin ? <StudioSidebar /> : <Header />}

      <div className={isStudioAdmin ? "lg:pl-72" : "pt-16"}>
        {isStudioAdmin && (
          <div className="sticky top-0 z-40 bg-background/95 backdrop-blur-md border-b border-border lg:hidden">
            <div className="px-4 py-4">
              <h1 className="text-xl font-bold text-foreground">SDK Integration</h1>
            </div>
          </div>
        )}

        <main className={isStudioAdmin ? "p-4 lg:p-8 pt-20 lg:pt-8 pb-20 lg:pb-8" : "container mx-auto px-4 lg:px-8 py-8 max-w-6xl pb-20"}>
          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-foreground mb-2">
                Solis Games SDK {!isStudioAdmin && "Documentation"}
              </h1>
              <p className="text-muted-foreground">
                Integrate your games with our platform for analytics, monetization, and more
              </p>
            </div>

            <div className="flex items-center space-x-3 mt-4 lg:mt-0">
              {isStudioAdmin && (
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Key"
                  onClick={() => navigate('/studio-api-keys')}
                >
                  Manage API Keys
                </Button>
              )}
              <Button
                variant={isStudioAdmin ? "default" : "outline"}
                size="sm"
                iconName="Github"
                onClick={() => window.open('https://github.com/solis-interactive/solisgames-sdk', '_blank')}
              >
                {isStudioAdmin ? "GitHub" : "View on GitHub"}
              </Button>
              {!isStudioAdmin && (
                <Button
                  variant="default"
                  size="sm"
                  iconName="LayoutDashboard"
                  onClick={() => navigate('/studio-dashboard')}
                >
                  Studio Portal
                </Button>
              )}
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="border-b border-border mb-8">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setSelectedTab(tab.id)}
                  className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
                    selectedTab === tab.id
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                  }`}
                >
                  <Icon name={tab.icon} size={16} />
                  <span>{tab.name}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          {selectedTab === 'overview' && (
            <div className="space-y-8">
              {/* Features Overview */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-card border border-border rounded-lg p-6 text-center">
                  <Icon name="Shield" size={32} className="text-primary mx-auto mb-3" />
                  <h3 className="font-semibold text-foreground mb-2">Site Lock</h3>
                  <p className="text-sm text-muted-foreground">
                    Protect your games from unauthorized hosting
                  </p>
                </div>
                <div className="bg-card border border-border rounded-lg p-6 text-center">
                  <Icon name="BarChart3" size={32} className="text-accent mx-auto mb-3" />
                  <h3 className="font-semibold text-foreground mb-2">Analytics</h3>
                  <p className="text-sm text-muted-foreground">
                    Track player behavior and game performance
                  </p>
                </div>
                <div className="bg-card border border-border rounded-lg p-6 text-center">
                  <Icon name="DollarSign" size={32} className="text-secondary mx-auto mb-3" />
                  <h3 className="font-semibold text-foreground mb-2">Monetization</h3>
                  <p className="text-sm text-muted-foreground">
                    Ads and in-app purchases integration
                  </p>
                </div>
                <div className="bg-card border border-border rounded-lg p-6 text-center">
                  <Icon name="Cloud" size={32} className="text-primary mx-auto mb-3" />
                  <h3 className="font-semibold text-foreground mb-2">Cloud Saves</h3>
                  <p className="text-sm text-muted-foreground">
                    Cross-device game progress storage
                  </p>
                </div>
                <div className="bg-card border border-border rounded-lg p-6 text-center relative">
                  <div className="absolute top-2 right-2">
                    <span className="px-2 py-1 text-xs font-bold bg-gradient-to-r from-primary to-purple-500 text-white rounded-full">
                      NEW
                    </span>
                  </div>
                  <Icon name="Trophy" size={32} className="text-orange-500 mx-auto mb-3" />
                  <h3 className="font-semibold text-foreground mb-2">Leaderboards</h3>
                  <p className="text-sm text-muted-foreground">
                    Realtime competitive rankings with anti-cheat
                  </p>
                </div>
                <div className="bg-card border border-border rounded-lg p-6 text-center relative">
                  <div className="absolute top-2 right-2">
                    <span className="px-2 py-1 text-xs font-bold bg-gradient-to-r from-primary to-purple-500 text-white rounded-full">
                      NEW
                    </span>
                  </div>
                  <Icon name="Award" size={32} className="text-pink-500 mx-auto mb-3" />
                  <h3 className="font-semibold text-foreground mb-2">Tournaments</h3>
                  <p className="text-sm text-muted-foreground">
                    Automated brackets and prize distribution
                  </p>
                </div>
                <div className="bg-card border border-border rounded-lg p-6 text-center relative">
                  <div className="absolute top-2 right-2">
                    <span className="px-2 py-1 text-xs font-bold bg-gradient-to-r from-primary to-purple-500 text-white rounded-full">
                      NEW
                    </span>
                  </div>
                  <Icon name="Star" size={32} className="text-purple-500 mx-auto mb-3" />
                  <h3 className="font-semibold text-foreground mb-2">Achievements</h3>
                  <p className="text-sm text-muted-foreground">
                    Xbox Live-style achievements with XP progression
                  </p>
                </div>
                <div className="bg-card border border-border rounded-lg p-6 text-center relative">
                  <div className="absolute top-2 right-2">
                    <span className="px-2 py-1 text-xs font-bold bg-gradient-to-r from-primary to-purple-500 text-white rounded-full">
                      NEW
                    </span>
                  </div>
                  <Icon name="Users" size={32} className="text-green-500 mx-auto mb-3" />
                  <h3 className="font-semibold text-foreground mb-2">Friends & Social</h3>
                  <p className="text-sm text-muted-foreground">
                    Platform-wide friends system with presence
                  </p>
                </div>
                <div className="bg-card border border-border rounded-lg p-6 text-center relative">
                  <div className="absolute top-2 right-2">
                    <span className="px-2 py-1 text-xs font-bold bg-gradient-to-r from-primary to-purple-500 text-white rounded-full">
                      NEW
                    </span>
                  </div>
                  <Icon name="Circle" size={32} className="text-emerald-500 mx-auto mb-3" />
                  <h3 className="font-semibold text-foreground mb-2">Presence System</h3>
                  <p className="text-sm text-muted-foreground">
                    Real-time player status with auto-heartbeat
                  </p>
                </div>
              </div>

              {/* Quick Start */}
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">Quick Start</h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold">1</div>
                    <div>
                      <p className="font-medium text-foreground">Get your API key</p>
                      <p className="text-sm text-muted-foreground">Generate an API key from your studio dashboard</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold">2</div>
                    <div>
                      <p className="font-medium text-foreground">Add the SDK to your game</p>
                      <p className="text-sm text-muted-foreground">Include our JavaScript SDK in your game's HTML</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold">3</div>
                    <div>
                      <p className="font-medium text-foreground">Initialize the SDK</p>
                      <p className="text-sm text-muted-foreground">Call the init function with your API key</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold">4</div>
                    <div>
                      <p className="font-medium text-foreground">Test and deploy</p>
                      <p className="text-sm text-muted-foreground">Test locally and deploy to Solis Games</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {selectedTab === 'javascript' && (
            <div className="space-y-8">
              {/* Installation */}
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">Installation</h3>
                <p className="text-muted-foreground mb-4">
                  Add the Solis Games SDK to your HTML file:
                </p>
                <div className="bg-muted rounded-lg p-4 relative">
                  <pre className="text-sm text-foreground overflow-x-auto">
                    <code>{`<script src="https://solisgames.com/solis-games-sdk-v1.js"></script>`}</code>
                  </pre>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Copy"
                    className="absolute top-2 right-2"
                    onClick={() => copyToClipboard('<script src="https://solisgames.com/solis-games-sdk-v1.js"></script>', 'install')}
                  >
                    {copySuccess === 'install' ? 'Copied!' : 'Copy'}
                  </Button>
                </div>
              </div>

              {/* Initialization */}
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">Initialization</h3>
                <p className="text-muted-foreground mb-4">
                  Initialize the SDK with your API key:
                </p>
                <div className="bg-muted rounded-lg p-4 relative">
                  <pre className="text-sm text-foreground overflow-x-auto">
                    <code>{`// Initialize the SDK
await window.SolisGames.SDK.init('${primaryApiKey}', {
  gameId: 'your-game-id' // Optional
});

// Check if initialization was successful
if (window.SolisGames.SDK.isInitialized) {
  console.log('SDK initialized successfully!');
}`}</code>
                  </pre>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Copy"
                    className="absolute top-2 right-2"
                    onClick={() => copyToClipboard(`await window.SolisGames.SDK.init('${primaryApiKey}', {\n  gameId: 'your-game-id'\n});`, 'init')}
                  >
                    {copySuccess === 'init' ? 'Copied!' : 'Copy'}
                  </Button>
                </div>
              </div>

              {/* Analytics */}
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">Analytics</h3>
                <p className="text-muted-foreground mb-4">
                  Track game events and player behavior:
                </p>
                <div className="bg-muted rounded-lg p-4 relative">
                  <pre className="text-sm text-foreground overflow-x-auto">
                    <code>{`// Track game start
window.SolisGames.SDK.game.gameplayStart();

// Track game end
window.SolisGames.SDK.game.gameplayStop();

// Track custom events
window.SolisGames.SDK.analytics.track('level_complete', {
  level: 5,
  score: 1250,
  time: 120
});

// Track happy time moments
window.SolisGames.SDK.game.happyTime();`}</code>
                  </pre>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Copy"
                    className="absolute top-2 right-2"
                    onClick={() => copyToClipboard(`window.SolisGames.SDK.game.gameplayStart();\nwindow.SolisGames.SDK.analytics.track('level_complete', {\n  level: 5,\n  score: 1250\n});`, 'analytics')}
                  >
                    {copySuccess === 'analytics' ? 'Copied!' : 'Copy'}
                  </Button>
                </div>
              </div>

              {/* Ads */}
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">Advertisement Integration</h3>
                <p className="text-muted-foreground mb-4">
                  Show ads to monetize your game:
                </p>
                <div className="bg-muted rounded-lg p-4 relative">
                  <pre className="text-sm text-foreground overflow-x-auto">
                    <code>{`// Show rewarded ad
try {
  const result = await window.SolisGames.SDK.ad.requestRewarded();
  if (result.success && result.reward) {
    // Give player reward
    givePlayerReward();
  }
} catch (error) {
  console.log('Ad failed to show:', error);
}

// Show interstitial ad
await window.SolisGames.SDK.ad.requestInterstitial();

// Show banner ad
await window.SolisGames.SDK.ad.showBanner('bottom');`}</code>
                  </pre>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Copy"
                    className="absolute top-2 right-2"
                    onClick={() => copyToClipboard(`const result = await window.SolisGames.SDK.ad.requestRewarded();\nif (result.success && result.reward) {\n  givePlayerReward();\n}`, 'ads')}
                  >
                    {copySuccess === 'ads' ? 'Copied!' : 'Copy'}
                  </Button>
                </div>
              </div>

              {/* Cloud Saves */}
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">Cloud Saves</h3>
                <p className="text-muted-foreground mb-4">
                  Save and load player progress across devices:
                </p>
                <div className="bg-muted rounded-lg p-4 relative">
                  <pre className="text-sm text-foreground overflow-x-auto">
                    <code>{`// Save player data
await window.SolisGames.SDK.data.save('playerProgress', {
  level: 10,
  score: 5000,
  items: ['sword', 'shield']
});

// Load player data
const progress = await window.SolisGames.SDK.data.load('playerProgress');
if (progress) {
  // Restore player progress
  restoreProgress(progress);
}

// Delete saved data
await window.SolisGames.SDK.data.delete('playerProgress');`}</code>
                  </pre>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Copy"
                    className="absolute top-2 right-2"
                    onClick={() => copyToClipboard(`await window.SolisGames.SDK.data.save('playerProgress', {\n  level: 10,\n  score: 5000\n});\n\nconst progress = await window.SolisGames.SDK.data.load('playerProgress');`, 'saves')}
                  >
                    {copySuccess === 'saves' ? 'Copied!' : 'Copy'}
                  </Button>
                </div>
              </div>

              {/* Leaderboards - NEW */}
              <div className="bg-gradient-to-br from-orange-500/5 via-pink-500/5 to-purple-500/5 border border-orange-500/20 rounded-lg p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="Trophy" size={24} className="text-orange-500" />
                  <h3 className="text-lg font-semibold text-foreground">Leaderboards</h3>
                  <span className="px-2 py-1 text-xs font-bold bg-gradient-to-r from-primary to-purple-500 text-white rounded-full">
                    NEW
                  </span>
                </div>
                <p className="text-muted-foreground mb-4">
                  Add competitive gaming with realtime leaderboards, automatic anti-cheat, and rank tracking:
                </p>
                <div className="bg-background/80 backdrop-blur rounded-lg p-4 relative">
                  <pre className="text-sm text-foreground overflow-x-auto">
                    <code>{`// Submit score (auto-creates leaderboard if it doesn't exist)
await window.SolisGames.SDK.leaderboards.submit('high_scores', {
  score: 9500,
  metadata: { level: 5, time: 120 }
});

// Get top rankings
const rankings = await window.SolisGames.SDK.leaderboards.get('high_scores', {
  scope: 'daily',    // 'global', 'daily', 'weekly', 'monthly'
  limit: 100,        // Number of entries to fetch
  offset: 0          // Pagination offset
});

// Get nearby ranks (5 above and 5 below current player)
const nearby = await window.SolisGames.SDK.leaderboards.nearby('high_scores', {
  range: 5
});

// Listen for realtime rank changes (WebSocket)
const unsubscribe = window.SolisGames.SDK.leaderboards.onRankChange((data) => {
  console.log(\`You're now rank #\${data.rank}!\`);
  console.log(\`New score: \${data.score}\`);
  updateUI(data);
});

// Stop listening when done
unsubscribe();`}</code>
                  </pre>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Copy"
                    className="absolute top-2 right-2"
                    onClick={() => copyToClipboard(`await window.SolisGames.SDK.leaderboards.submit('high_scores', { score: 9500 });\nconst rankings = await window.SolisGames.SDK.leaderboards.get('high_scores');`, 'leaderboards')}
                  >
                    {copySuccess === 'leaderboards' ? 'Copied!' : 'Copy'}
                  </Button>
                </div>

                <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <Icon name="Shield" size={20} className="text-blue-600 dark:text-blue-400 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-blue-900 dark:text-blue-300">Built-in Anti-Cheat</h4>
                      <p className="text-sm text-blue-700 dark:text-blue-400 mt-1">
                        Server-side validation with rate limiting (10/min), playtime checks, and Z-score outlier detection. Suspicious scores are auto-flagged for review in your studio dashboard.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tournaments - NEW */}
              <div className="bg-gradient-to-br from-orange-500/5 via-pink-500/5 to-purple-500/5 border border-pink-500/20 rounded-lg p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="Award" size={24} className="text-pink-500" />
                  <h3 className="text-lg font-semibold text-foreground">Tournaments</h3>
                  <span className="px-2 py-1 text-xs font-bold bg-gradient-to-r from-primary to-purple-500 text-white rounded-full">
                    NEW
                  </span>
                </div>
                <p className="text-muted-foreground mb-4">
                  Create automated tournaments with bracket generation, prize pools, and player matchmaking:
                </p>
                <div className="bg-background/80 backdrop-blur rounded-lg p-4 relative">
                  <pre className="text-sm text-foreground overflow-x-auto">
                    <code>{`// Create a tournament
await window.SolisGames.SDK.tournaments.create({
  name: 'Weekend Blitz Championship',
  description: 'Compete for glory and prizes!',
  bracketType: 'single_elimination', // or 'swiss', 'round_robin'
  maxParticipants: 64,
  entryFeeCoins: 100,              // Optional entry fee
  prizePool: {
    '1st': 10000,
    '2nd': 5000,
    '3rd': 2500
  },
  durationHours: 72,              // Tournament duration
  leaderboardKey: 'high_scores'   // Optional: link to leaderboard
});

// Join a tournament
await window.SolisGames.SDK.tournaments.join('tournament_id');

// List active tournaments
const tournaments = await window.SolisGames.SDK.tournaments.list({
  status: 'active',  // 'pending', 'active', 'completed', 'cancelled', 'all'
  limit: 10,
  offset: 0
});

// Get tournament details with participants and matches
const details = await window.SolisGames.SDK.tournaments.get('tournament_id');
console.log('Participants:', details.participants);
console.log('Matches:', details.matches);
console.log('Winner:', details.winner);`}</code>
                  </pre>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Copy"
                    className="absolute top-2 right-2"
                    onClick={() => copyToClipboard(`await window.SolisGames.SDK.tournaments.create({\n  name: 'Weekend Blitz',\n  durationHours: 72,\n  prizePool: { '1st': 10000 }\n});`, 'tournaments')}
                  >
                    {copySuccess === 'tournaments' ? 'Copied!' : 'Copy'}
                  </Button>
                </div>

                <div className="mt-4 p-4 bg-purple-50 dark:bg-purple-950/20 border border-purple-200 dark:border-purple-800 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <Icon name="Zap" size={20} className="text-purple-600 dark:text-purple-400 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-purple-900 dark:text-purple-300">Fully Automated</h4>
                      <p className="text-sm text-purple-700 dark:text-purple-400 mt-1">
                        Brackets are auto-generated, matches are scheduled, and prizes are distributed automatically. Tournament status updates every 5 minutes via background worker.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Achievements - NEW */}
              <div className="bg-gradient-to-br from-purple-500/5 via-indigo-500/5 to-blue-500/5 border border-purple-500/20 rounded-lg p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="Star" size={24} className="text-purple-500" />
                  <h3 className="text-lg font-semibold text-foreground">Achievements</h3>
                  <span className="px-2 py-1 text-xs font-bold bg-gradient-to-r from-primary to-purple-500 text-white rounded-full">
                    NEW
                  </span>
                </div>
                <p className="text-muted-foreground mb-4">
                  Xbox Live-style achievements with cross-game XP progression, automatic rarity calculation, and progress tracking:
                </p>
                <div className="bg-background/80 backdrop-blur rounded-lg p-4 relative">
                  <pre className="text-sm text-foreground overflow-x-auto">
                    <code>{`// Define achievements (call once at game start)
window.SolisGames.SDK.achievements.define([
  {
    id: 'first_win',
    name: 'First Victory',
    description: 'Win your first game',
    xp: 100
  },
  {
    id: 'speedrunner',
    name: 'Speedrunner',
    description: 'Complete level in under 60 seconds',
    xp: 500,
    rare: true
  }
]);

// Unlock achievement (auto-grants XP)
const result = await window.SolisGames.SDK.achievements.unlock('first_win');
console.log(\`Earned \${result.xpEarned} XP!\`);
console.log(\`Total XP: \${result.totalXP}, Level: \${result.level}\`);
console.log(\`Rarity: \${result.rarity}\`); // 'common', 'uncommon', 'rare'

if (result.leveledUp) {
  showLevelUpAnimation();
}

// Track progress (75/100 coins collected)
const progress = await window.SolisGames.SDK.achievements.progress('coin_collector', {
  current: 75,
  target: 100
});
console.log(\`Progress: \${progress.percent}%\`);

if (progress.completed) {
  console.log('Achievement auto-unlocked!');
}

// Get player stats
const stats = await window.SolisGames.SDK.achievements.getPlayerStats();
console.log(\`Level \${stats.level} - \${stats.totalXP} XP\`);
console.log(\`Achievements: \${stats.achievementsUnlocked}/\${stats.totalAchievements}\`);

// Listen for unlocks
const unsubscribe = window.SolisGames.SDK.achievements.onUnlock((achievement) => {
  showAchievementToast({
    name: achievement.achievement.name,
    xp: achievement.xpEarned,
    rarity: achievement.rarity
  });
});`}</code>
                  </pre>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Copy"
                    className="absolute top-2 right-2"
                    onClick={() => copyToClipboard(`await window.SolisGames.SDK.achievements.unlock('first_win');`, 'achievements')}
                  >
                    {copySuccess === 'achievements' ? 'Copied!' : 'Copy'}
                  </Button>
                </div>

                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-purple-50 dark:bg-purple-950/20 border border-purple-200 dark:border-purple-800 rounded-lg">
                    <div className="flex items-start space-x-3">
                      <Icon name="TrendingUp" size={20} className="text-purple-600 dark:text-purple-400 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-purple-900 dark:text-purple-300 text-sm">Cross-Game XP</h4>
                        <p className="text-xs text-purple-700 dark:text-purple-400 mt-1">
                          XP earned in any game counts toward player profile level. Level = floor(sqrt(totalXP / 100))
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <div className="flex items-start space-x-3">
                      <Icon name="Sparkles" size={20} className="text-blue-600 dark:text-blue-400 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-blue-900 dark:text-blue-300 text-sm">Auto Rarity</h4>
                        <p className="text-xs text-blue-700 dark:text-blue-400 mt-1">
                          Common (&gt;20%), Uncommon (5-20%), Rare (&lt;5%) automatically calculated hourly
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Friends & Social Graph - NEW */}
              <div className="bg-gradient-to-br from-green-500/5 via-teal-500/5 to-cyan-500/5 border border-green-500/20 rounded-lg p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="Users" size={24} className="text-green-500" />
                  <h3 className="text-lg font-semibold text-foreground">Friends & Social Graph</h3>
                  <span className="px-2 py-1 text-xs font-bold bg-gradient-to-r from-primary to-purple-500 text-white rounded-full">
                    NEW
                  </span>
                </div>
                <p className="text-muted-foreground mb-4">
                  Steam-style friends system with platform-wide social graph. Players stay connected across ALL games (like Xbox Live):
                </p>
                <div className="bg-background/80 backdrop-blur rounded-lg p-4 relative">
                  <pre className="text-sm text-foreground overflow-x-auto">
                    <code>{`// Send friend request with optional message
await window.SolisGames.SDK.friends.sendRequest('user_uuid', 'Great game!');

// Get pending requests (received and sent)
const requests = await window.SolisGames.SDK.friends.getPendingRequests();
console.log(\`Received: \${requests.total.received}\`);
console.log(\`Sent: \${requests.total.sent}\`);

// Accept friend request
await window.SolisGames.SDK.friends.acceptRequest('request_id');

// Get friends list with real-time presence
const friends = await window.SolisGames.SDK.friends.list();
console.log(\`Total friends: \${friends.total}\`);
console.log(\`Online: \${friends.counts.online}\`);
console.log(\`Playing: \${friends.counts.playing}\`);

// Friends are sorted: playing > online > away > offline
friends.friends.forEach(friend => {
  console.log(\`\${friend.fullName} - \${friend.presence.status}\`);
  if (friend.presence.status === 'playing') {
    console.log(\`  Playing: \${friend.presence.gameName}\`);
    console.log(\`  Status: \${friend.presence.customStatus}\`);
    console.log(\`  Joinable: \${friend.presence.joinable}\`);
  }
});

// Filter by status
const onlineFriends = await window.SolisGames.SDK.friends.list({ status: 'online' });
const playingFriends = await window.SolisGames.SDK.friends.list({ status: 'playing' });

// Remove friend
await window.SolisGames.SDK.friends.remove('user_uuid');

// Block user
await window.SolisGames.SDK.friends.block('user_uuid', 'Reason');

// Listen for friend events
const unsubscribe = window.SolisGames.SDK.friends.onEvent('request_received', (data) => {
  showNotification(\`\${data.fromUser.fullName} sent you a friend request!\`);
});`}</code>
                  </pre>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Copy"
                    className="absolute top-2 right-2"
                    onClick={() => copyToClipboard(`await window.SolisGames.SDK.friends.sendRequest('user_uuid', 'Great game!');`, 'friends')}
                  >
                    {copySuccess === 'friends' ? 'Copied!' : 'Copy'}
                  </Button>
                </div>

                <div className="mt-4 p-4 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <Icon name="Globe" size={20} className="text-green-600 dark:text-green-400 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-green-900 dark:text-green-300">Platform-Wide Social</h4>
                      <p className="text-sm text-green-700 dark:text-green-400 mt-1">
                        Friends work across ALL games and studios. Auto-accept if both players request each other. Presence includes game info, custom status, and joinability.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Presence System - NEW */}
              <div className="bg-gradient-to-br from-emerald-500/5 via-lime-500/5 to-green-500/5 border border-emerald-500/20 rounded-lg p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="Circle" size={24} className="text-emerald-500" />
                  <h3 className="text-lg font-semibold text-foreground">Presence System</h3>
                  <span className="px-2 py-1 text-xs font-bold bg-gradient-to-r from-primary to-purple-500 text-white rounded-full">
                    NEW
                  </span>
                </div>
                <p className="text-muted-foreground mb-4">
                  Real-time presence with automatic heartbeat. Show what players are doing across all games:
                </p>
                <div className="bg-background/80 backdrop-blur rounded-lg p-4 relative">
                  <pre className="text-sm text-foreground overflow-x-auto">
                    <code>{`// Set presence to playing with custom status
await window.SolisGames.SDK.presence.set('playing', {
  customStatus: 'Level 5 - Boss Fight',
  joinable: true  // Friends can join this session
});

// Set status to online
await window.SolisGames.SDK.presence.set('online');

// Set status to away
await window.SolisGames.SDK.presence.set('away');

// Set status to offline (when player closes game)
await window.SolisGames.SDK.presence.set('offline');

// Get presence for specific users
const presence = await window.SolisGames.SDK.presence.get(['user_uuid_1', 'user_uuid_2']);
presence.presence.forEach(user => {
  console.log(\`\${user.userId}: \${user.status}\`);
  if (user.status === 'playing') {
    console.log(\`  Game: \${user.gameName}\`);
    console.log(\`  Status: \${user.customStatus}\`);
    console.log(\`  Joinable: \${user.joinable}\`);
  }
  console.log(\`  Last seen: \${user.lastSeen}\`);
});

// Auto-update presence every 30 seconds
window.SolisGames.SDK.presence.startHeartbeat();

// Stop heartbeat when player closes game
window.SolisGames.SDK.presence.stopHeartbeat();

// Listen for friends' presence changes
const unsubscribe = window.SolisGames.SDK.presence.onChange((data) => {
  console.log(\`\${data.user.fullName} is now \${data.presence.status}\`);
  if (data.presence.status === 'playing') {
    console.log(\`Playing: \${data.presence.gameName}\`);
  }
  updateFriendsList();
});`}</code>
                  </pre>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Copy"
                    className="absolute top-2 right-2"
                    onClick={() => copyToClipboard(`await window.SolisGames.SDK.presence.set('playing', { customStatus: 'Level 5', joinable: true });`, 'presence')}
                  >
                    {copySuccess === 'presence' ? 'Copied!' : 'Copy'}
                  </Button>
                </div>

                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800 rounded-lg">
                    <div className="flex items-start space-x-3">
                      <Icon name="Activity" size={20} className="text-emerald-600 dark:text-emerald-400 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-emerald-900 dark:text-emerald-300 text-sm">Auto Heartbeat</h4>
                        <p className="text-xs text-emerald-700 dark:text-emerald-400 mt-1">
                          Presence auto-updates every 30s to keep status fresh and prevent appearing offline
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 bg-lime-50 dark:bg-lime-950/20 border border-lime-200 dark:border-lime-800 rounded-lg">
                    <div className="flex items-start space-x-3">
                      <Icon name="Bell" size={20} className="text-lime-600 dark:text-lime-400 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-lime-900 dark:text-lime-300 text-sm">Realtime Events</h4>
                        <p className="text-xs text-lime-700 dark:text-lime-400 mt-1">
                          Get notified instantly when friends start/stop playing, go online/offline
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {selectedTab === 'unity' && (
            <div className="space-y-8">
              {/* Installation */}
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                  <Icon name="Download" size={20} className="mr-2" />
                  Installation
                </h3>
                <p className="text-muted-foreground mb-6">
                  Install the Solis Games Unity SDK via Unity Package Manager using a Git URL.
                </p>

                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-foreground mb-2">Method 1: Package Manager (Recommended)</h4>
                    <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground mb-3">
                      <li>Open Unity Editor</li>
                      <li>Go to <code className="bg-muted px-1 py-0.5 rounded text-xs">Window → Package Manager</code></li>
                      <li>Click the <code className="bg-muted px-1 py-0.5 rounded text-xs">+</code> button → <code className="bg-muted px-1 py-0.5 rounded text-xs">Add package from git URL...</code></li>
                      <li>Paste the URL below</li>
                    </ol>
                    <div className="bg-muted rounded-lg p-4 relative">
                      <pre className="text-sm text-foreground overflow-x-auto">
                        <code>https://github.com/solis-interactive/solisgames-sdk.git</code>
                      </pre>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="absolute top-2 right-2"
                        onClick={() => copyToClipboard('https://github.com/solis-interactive/solisgames-sdk.git', 'unityGitUrl')}
                      >
                        <Icon name={copySuccess === 'unityGitUrl' ? 'Check' : 'Copy'} size={16} />
                      </Button>
                    </div>
                  </div>

                  <div className="p-4 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <div className="flex items-start space-x-3">
                      <Icon name="Info" size={20} className="text-blue-600 dark:text-blue-400 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-blue-900 dark:text-blue-100">Requirements</h4>
                        <ul className="text-sm text-blue-700 dark:text-blue-300 mt-1 space-y-1">
                          <li>• Unity 2022.3 LTS or newer</li>
                          <li>• WebGL Build Support installed</li>
                          <li>• Git installed on your system</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Configuration */}
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                  <Icon name="Settings" size={20} className="mr-2" />
                  Configuration
                </h3>
                <p className="text-muted-foreground mb-6">
                  Configure your API key and game settings in the Unity Editor.
                </p>

                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-foreground mb-2">Step 1: Open SDK Settings</h4>
                    <p className="text-sm text-muted-foreground mb-2">
                      Go to <code className="bg-muted px-1 py-0.5 rounded text-xs">Window → Solis Games → SDK Settings</code>
                    </p>
                  </div>

                  <div>
                    <h4 className="font-medium text-foreground mb-2">Step 2: Enter Your API Key</h4>
                    <div className="bg-muted rounded-lg p-4 mb-2">
                      <div className="flex items-center justify-between">
                        <code className="text-sm text-foreground">{primaryApiKey}</code>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(primaryApiKey, 'unityApiKey')}
                        >
                          <Icon name={copySuccess === 'unityApiKey' ? 'Check' : 'Copy'} size={16} />
                        </Button>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">Your Production API Key - copy and paste into the SDK Settings window</p>
                  </div>

                  <div>
                    <h4 className="font-medium text-foreground mb-2">Step 3: Configure Features</h4>
                    <p className="text-sm text-muted-foreground">
                      Enable or disable SDK features (ads, analytics, leaderboards, etc.) based on your game's needs.
                    </p>
                  </div>
                </div>
              </div>

              {/* Quick Start */}
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                  <Icon name="Zap" size={20} className="mr-2" />
                  Quick Start
                </h3>
                <p className="text-muted-foreground mb-6">
                  Initialize the SDK and start using features in your Unity game.
                </p>

                <div className="bg-muted rounded-lg p-4 relative mb-4">
                  <pre className="text-sm text-foreground overflow-x-auto">
                    <code>{`using UnityEngine;
using SolisGames;

public class GameManager : MonoBehaviour
{
    async void Start()
    {
        // Initialize SDK
        bool success = await SolisSDK.InitAsync("${primaryApiKey}");

        if (success)
        {
            Debug.Log("✅ Solis Games SDK initialized!");

            // Get user data
            var user = await SolisSDK.User.GetUserAsync();
            Debug.Log($"Welcome, {user.username}!");

            // Track analytics event
            SolisSDK.Analytics.TrackEvent("game_started");
        }
        else
        {
            Debug.LogError("SDK initialization failed");
        }
    }
}`}</code>
                  </pre>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute top-2 right-2"
                    onClick={() => copyToClipboard(`using UnityEngine;
using SolisGames;

public class GameManager : MonoBehaviour
{
    async void Start()
    {
        // Initialize SDK
        bool success = await SolisSDK.InitAsync("${primaryApiKey}");

        if (success)
        {
            Debug.Log("✅ Solis Games SDK initialized!");

            // Get user data
            var user = await SolisSDK.User.GetUserAsync();
            Debug.Log($"Welcome, {user.username}!");

            // Track analytics event
            SolisSDK.Analytics.TrackEvent("game_started");
        }
        else
        {
            Debug.LogError("SDK initialization failed");
        }
    }
}`, 'unityQuickStart')}
                  >
                    <Icon name={copySuccess === 'unityQuickStart' ? 'Check' : 'Copy'} size={16} />
                  </Button>
                </div>

                <div className="p-4 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <Icon name="CheckCircle" size={20} className="text-green-600 dark:text-green-400 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-green-900 dark:text-green-100">Editor Testing</h4>
                      <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                        The SDK works in Unity Editor with mock data - no WebGL build required for testing!
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Core Features */}
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                  <Icon name="BookOpen" size={20} className="mr-2" />
                  Core Features
                </h3>

                <div className="space-y-6">
                  {/* Leaderboards */}
                  <div>
                    <h4 className="font-medium text-foreground mb-2 flex items-center">
                      <Icon name="Trophy" size={16} className="mr-2 text-yellow-500" />
                      Leaderboards & Rankings
                    </h4>
                    <div className="bg-muted rounded-lg p-4 relative">
                      <pre className="text-sm text-foreground overflow-x-auto">
                        <code>{`// Submit score
var result = await SolisSDK.Leaderboards.SubmitAsync("high_scores", 1000);
Debug.Log($"Your rank: #{result.rank}");

// Get top 10
var rankings = await SolisSDK.Leaderboards.GetAsync("high_scores", limit: 10);
foreach (var entry in rankings.entries)
{
    Debug.Log($"#{entry.rank} - {entry.username}: {entry.score}");
}`}</code>
                      </pre>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="absolute top-2 right-2"
                        onClick={() => copyToClipboard(`// Submit score
var result = await SolisSDK.Leaderboards.SubmitAsync("high_scores", 1000);
Debug.Log($"Your rank: #{result.rank}");

// Get top 10
var rankings = await SolisSDK.Leaderboards.GetAsync("high_scores", limit: 10);
foreach (var entry in rankings.entries)
{
    Debug.Log($"#{entry.rank} - {entry.username}: {entry.score}");
}`, 'unityLeaderboards')}
                      >
                        <Icon name={copySuccess === 'unityLeaderboards' ? 'Check' : 'Copy'} size={16} />
                      </Button>
                    </div>
                  </div>

                  {/* Achievements */}
                  <div>
                    <h4 className="font-medium text-foreground mb-2 flex items-center">
                      <Icon name="Award" size={16} className="mr-2 text-purple-500" />
                      Achievements & Progression
                    </h4>
                    <div className="bg-muted rounded-lg p-4 relative">
                      <pre className="text-sm text-foreground overflow-x-auto">
                        <code>{`// Unlock achievement
var achievement = await SolisSDK.Achievements.UnlockAsync("first_win");
Debug.Log($"🏆 Unlocked: {achievement.name} (+{achievement.xp} XP)");

// Track progress
await SolisSDK.Achievements.ProgressAsync("coin_collector", 150, 1000);

// Get player stats
var stats = await SolisSDK.Achievements.GetStatsAsync();
Debug.Log($"Level {stats.level} - {stats.totalXP} XP");`}</code>
                      </pre>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="absolute top-2 right-2"
                        onClick={() => copyToClipboard(`// Unlock achievement
var achievement = await SolisSDK.Achievements.UnlockAsync("first_win");
Debug.Log($"🏆 Unlocked: {achievement.name} (+{achievement.xp} XP)");

// Track progress
await SolisSDK.Achievements.ProgressAsync("coin_collector", 150, 1000);

// Get player stats
var stats = await SolisSDK.Achievements.GetStatsAsync();
Debug.Log($"Level {stats.level} - {stats.totalXP} XP");`, 'unityAchievements')}
                      >
                        <Icon name={copySuccess === 'unityAchievements' ? 'Check' : 'Copy'} size={16} />
                      </Button>
                    </div>
                  </div>

                  {/* Cloud Saves */}
                  <div>
                    <h4 className="font-medium text-foreground mb-2 flex items-center">
                      <Icon name="Cloud" size={16} className="mr-2 text-blue-500" />
                      Cloud Saves
                    </h4>
                    <div className="bg-muted rounded-lg p-4 relative">
                      <pre className="text-sm text-foreground overflow-x-auto">
                        <code>{`[System.Serializable]
public class PlayerSave
{
    public int level;
    public int coins;
    public List<string> unlockedLevels;
}

// Save
var save = new PlayerSave { level = 10, coins = 500 };
await SolisSDK.CloudSave.SaveAsync("player_progress", save);

// Load
var loadedSave = await SolisSDK.CloudSave.LoadAsync<PlayerSave>("player_progress");
Debug.Log($"Loaded: Level {loadedSave.level}");`}</code>
                      </pre>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="absolute top-2 right-2"
                        onClick={() => copyToClipboard(`[System.Serializable]
public class PlayerSave
{
    public int level;
    public int coins;
    public List<string> unlockedLevels;
}

// Save
var save = new PlayerSave { level = 10, coins = 500 };
await SolisSDK.CloudSave.SaveAsync("player_progress", save);

// Load
var loadedSave = await SolisSDK.CloudSave.LoadAsync<PlayerSave>("player_progress");
Debug.Log($"Loaded: Level {loadedSave.level}");`, 'unityCloudSave')}
                      >
                        <Icon name={copySuccess === 'unityCloudSave' ? 'Check' : 'Copy'} size={16} />
                      </Button>
                    </div>
                  </div>

                  {/* Ads */}
                  <div>
                    <h4 className="font-medium text-foreground mb-2 flex items-center">
                      <Icon name="DollarSign" size={16} className="mr-2 text-green-500" />
                      Rewarded Ads
                    </h4>
                    <div className="bg-muted rounded-lg p-4 relative">
                      <pre className="text-sm text-foreground overflow-x-auto">
                        <code>{`// Show rewarded ad
bool adWatched = await SolisSDK.Ads.ShowRewardedAsync();

if (adWatched)
{
    // Grant reward to player
    Debug.Log("Reward granted!");
    GivePlayerCoins(100);
}
else
{
    Debug.Log("Ad was skipped or unavailable");
}`}</code>
                      </pre>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="absolute top-2 right-2"
                        onClick={() => copyToClipboard(`// Show rewarded ad
bool adWatched = await SolisSDK.Ads.ShowRewardedAsync();

if (adWatched)
{
    // Grant reward to player
    Debug.Log("Reward granted!");
    GivePlayerCoins(100);
}
else
{
    Debug.Log("Ad was skipped or unavailable");
}`, 'unityAds')}
                      >
                        <Icon name={copySuccess === 'unityAds' ? 'Check' : 'Copy'} size={16} />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Build & Deploy */}
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                  <Icon name="Hammer" size={20} className="mr-2" />
                  Build & Deploy
                </h3>
                <p className="text-muted-foreground mb-6">
                  Build your WebGL game with automatic SDK integration.
                </p>

                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-foreground mb-2">Automatic SDK Injection</h4>
                    <p className="text-sm text-muted-foreground mb-3">
                      The SDK automatically injects the Solis Games JavaScript SDK into your WebGL build. No manual setup required!
                    </p>
                    <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                      <li>Go to <code className="bg-muted px-1 py-0.5 rounded text-xs">Window → Solis Games → Build & Deploy</code></li>
                      <li>Choose your build location</li>
                      <li>Click <code className="bg-muted px-1 py-0.5 rounded text-xs">Build WebGL</code></li>
                      <li>SDK script is automatically added to your index.html</li>
                    </ol>
                  </div>

                  <div className="p-4 bg-purple-50 dark:bg-purple-950/20 border border-purple-200 dark:border-purple-800 rounded-lg">
                    <div className="flex items-start space-x-3">
                      <Icon name="Sparkles" size={20} className="text-purple-600 dark:text-purple-400 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-purple-900 dark:text-purple-100">One-Click Build</h4>
                        <p className="text-sm text-purple-700 dark:text-purple-300 mt-1">
                          Use our Build & Deploy window for streamlined WebGL builds with compression options and auto-run features.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Resources */}
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                  <Icon name="ExternalLink" size={20} className="mr-2" />
                  Documentation & Examples
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <a
                    href="https://github.com/solis-interactive/solisgames-sdk"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-4 bg-muted hover:bg-muted/80 rounded-lg transition-colors group"
                  >
                    <div className="flex items-center space-x-3">
                      <Icon name="Github" size={20} className="text-muted-foreground" />
                      <div>
                        <h4 className="font-medium text-foreground">GitHub Repository</h4>
                        <p className="text-xs text-muted-foreground">View source code & releases</p>
                      </div>
                    </div>
                    <Icon name="ExternalLink" size={16} className="text-muted-foreground group-hover:text-foreground" />
                  </a>

                  <a
                    href="https://github.com/solis-interactive/solisgames-sdk#readme"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-4 bg-muted hover:bg-muted/80 rounded-lg transition-colors group"
                  >
                    <div className="flex items-center space-x-3">
                      <Icon name="FileText" size={20} className="text-muted-foreground" />
                      <div>
                        <h4 className="font-medium text-foreground">Full Documentation</h4>
                        <p className="text-xs text-muted-foreground">Complete API reference</p>
                      </div>
                    </div>
                    <Icon name="ExternalLink" size={16} className="text-muted-foreground group-hover:text-foreground" />
                  </a>

                  <a
                    href="https://github.com/solis-interactive/solisgames-sdk/tree/main/Samples~"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-4 bg-muted hover:bg-muted/80 rounded-lg transition-colors group"
                  >
                    <div className="flex items-center space-x-3">
                      <Icon name="Code" size={20} className="text-muted-foreground" />
                      <div>
                        <h4 className="font-medium text-foreground">Example Projects</h4>
                        <p className="text-xs text-muted-foreground">QuickStart & FullFeatures</p>
                      </div>
                    </div>
                    <Icon name="ExternalLink" size={16} className="text-muted-foreground group-hover:text-foreground" />
                  </a>

                  <a
                    href="https://github.com/solis-interactive/solisgames-sdk/blob/main/Documentation~/Troubleshooting.md"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-4 bg-muted hover:bg-muted/80 rounded-lg transition-colors group"
                  >
                    <div className="flex items-center space-x-3">
                      <Icon name="HelpCircle" size={20} className="text-muted-foreground" />
                      <div>
                        <h4 className="font-medium text-foreground">Troubleshooting</h4>
                        <p className="text-xs text-muted-foreground">Common issues & solutions</p>
                      </div>
                    </div>
                    <Icon name="ExternalLink" size={16} className="text-muted-foreground group-hover:text-foreground" />
                  </a>

                  <a
                    href="https://discord.gg/TZdBFBhW"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-4 bg-muted hover:bg-muted/80 rounded-lg transition-colors group"
                  >
                    <div className="flex items-center space-x-3">
                      <Icon name="MessageCircle" size={20} className="text-muted-foreground" />
                      <div>
                        <h4 className="font-medium text-foreground">Discord Community</h4>
                        <p className="text-xs text-muted-foreground">Get help from the community</p>
                      </div>
                    </div>
                    <Icon name="ExternalLink" size={16} className="text-muted-foreground group-hover:text-foreground" />
                  </a>
                </div>
              </div>
            </div>
          )}

          {selectedTab === 'testing' && (
            <div className="space-y-8">
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">Testing Your Integration</h3>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="font-medium text-foreground mb-2">Local Testing</h4>
                    <p className="text-muted-foreground mb-3">
                      The SDK works on localhost and 127.0.0.1 for testing. Demo ads and analytics will be shown.
                    </p>
                    <div className="bg-muted rounded-lg p-4">
                      <pre className="text-sm text-foreground">
                        <code>http://localhost:3000/your-game</code>
                      </pre>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium text-foreground mb-2">Domain Whitelist</h4>
                    <p className="text-muted-foreground mb-3">
                      Add your development domains to the whitelist in API Keys settings.
                    </p>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => navigate('/studio-api-keys')}
                    >
                      Manage Whitelist
                    </Button>
                  </div>

                  <div>
                    <h4 className="font-medium text-foreground mb-2">Console Debugging</h4>
                    <p className="text-muted-foreground mb-3">
                      Check browser console for SDK logs and errors:
                    </p>
                    <div className="bg-muted rounded-lg p-4">
                      <pre className="text-sm text-foreground">
                        <code>{`// Enable debug logging
window.SolisGames.SDK.debug = true;

// Check SDK status
console.log('SDK initialized:', window.SolisGames.SDK.isInitialized);
console.log('Environment:', window.SolisGames.SDK.environment);`}</code>
                      </pre>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {selectedTab === 'examples' && (
            <div className="space-y-8">
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">Complete Example</h3>
                <p className="text-muted-foreground mb-4">
                  Here's a complete example of integrating the Solis Games SDK:
                </p>
                
                <div className="bg-muted rounded-lg p-4 relative">
                  <pre className="text-sm text-foreground overflow-x-auto">
                    <code>{`<!DOCTYPE html>
<html>
<head>
    <title>My Awesome Game</title>
    <script src="https://solisgames.com/solis-games-sdk-v1.js"></script>
</head>
<body>
    <div id="game-container">
        <canvas id="game-canvas"></canvas>
        <button id="show-ad-btn">Watch Ad for Reward</button>
    </div>

    <script>
        let gameInitialized = false;
        
        // Initialize SDK
        async function initGame() {
            try {
                await window.SolisGames.SDK.init('${primaryApiKey}');
                console.log('SDK initialized!');
                
                // Track game loading
                window.SolisGames.SDK.game.loadingStart();
                
                // Initialize your game here
                await initializeGameEngine();
                
                window.SolisGames.SDK.game.loadingStop();
                window.SolisGames.SDK.game.gameplayStart();
                
                gameInitialized = true;
                
            } catch (error) {
                console.error('SDK initialization failed:', error);
            }
        }
        
        // Show rewarded ad
        document.getElementById('show-ad-btn').onclick = async () => {
            try {
                const result = await window.SolisGames.SDK.ad.requestRewarded();
                if (result.success && result.reward) {
                    // Give player coins or lives
                    givePlayerReward(100);
                    
                    // Track the reward
                    window.SolisGames.SDK.analytics.track('reward_earned', {
                        type: 'ad_reward',
                        amount: 100
                    });
                }
            } catch (error) {
                console.log('Ad not available:', error);
            }
        };
        
        // Save progress periodically
        function saveProgress() {
            if (!gameInitialized) return;
            
            const gameState = {
                level: currentLevel,
                score: playerScore,
                lives: playerLives
            };
            
            window.SolisGames.SDK.data.save('gameState', gameState);
        }
        
        // Load saved progress
        async function loadProgress() {
            const savedState = await window.SolisGames.SDK.data.load('gameState');
            if (savedState) {
                currentLevel = savedState.level;
                playerScore = savedState.score;
                playerLives = savedState.lives;
            }
        }
        
        // Initialize when page loads
        window.addEventListener('load', initGame);
        
        // Save progress every 30 seconds
        setInterval(saveProgress, 30000);
    </script>
</body>
</html>`}</code>
                  </pre>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Copy"
                    className="absolute top-2 right-2"
                    onClick={() => copyToClipboard('Complete HTML example copied to clipboard', 'example')}
                  >
                    {copySuccess === 'example' ? 'Copied!' : 'Copy'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Show Footer for non-studio users */}
      {!isStudioAdmin && <Footer />}
    </div>
  );
};

export default StudioSDK;
